import React, { MutableRefObject, useState, useEffect } from 'react';
import { isActionsColumn } from '@components-library/Grid/actions/isActionsColumn';
import { HeaderGroup, Table } from '@tanstack/react-table';
import cn from 'classnames';
import throttle from 'lodash-es/throttle';
import { GridHeaderCell } from './GridCell';
import styles from './grid.module.scss';

interface IGridCellsHeader<T> {
  tableWrapperRef: MutableRefObject<HTMLDivElement | null>;
  headerGroups: HeaderGroup<T>[];
  table: Table<T>;
  classNameHeaderCell?: string;
  rowSelectionEnabled?: boolean;
  columnResizeEnabled?: boolean;
}

export const GridCellsHeader = <T extends Record<string, unknown>>({
  tableWrapperRef,
  headerGroups,
  table,
  classNameHeaderCell,
  rowSelectionEnabled,
  columnResizeEnabled = true,
}: IGridCellsHeader<T>) => {
  const [tableWrapperHeight, setTableWrapperHeight] = useState(51);

  useEffect(() => {
    let currentHeight = -1;
    const resizeObserver = new ResizeObserver(
      throttle(
        (entries) => {
          const tableWrapperHeight = Math.floor(entries[0].contentRect.height);

          if (tableWrapperHeight !== currentHeight) {
            currentHeight = tableWrapperHeight;
            setTableWrapperHeight(tableWrapperHeight - 2);
          }
        },
        500,
        { leading: true, trailing: true },
      ),
    );

    if (columnResizeEnabled && tableWrapperRef.current) {
      resizeObserver.observe(tableWrapperRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [columnResizeEnabled, tableWrapperRef]);

  return (
    <>
      {headerGroups.map((headerGroup) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header, index) => (
            <GridHeaderCell
              key={header.id}
              table={table}
              header={header}
              isFirstCell={index === 0}
              classNameHeaderCell={cn(classNameHeaderCell, {
                [styles.actions]: isActionsColumn(header.id),
              })}
              size={header.getSize()}
              rowSelectionEnabled={rowSelectionEnabled}
              selectDisabled={false}
              selectChecked={table.getIsAllRowsSelected()}
              selectIndeterminate={table.getIsSomeRowsSelected()}
              columnResizeEnabled={columnResizeEnabled}
              tableHeight={tableWrapperHeight}
              isSorted={header.column.getIsSorted()}
            />
          ))}
        </tr>
      ))}
    </>
  );
};
